// InviteReview — review page listing every question + current answer.
// Click any answer row to edit inline; save commits via PATCH.

import { useEffect, useMemo, useState } from 'react'
import { useLocation, useParams } from 'wouter'
import {
  getInvitation,
  getStoredLang,
  saveAnswer,
  type Answer,
  type Pack,
  type PackQuestion,
} from '@/lib/api'
import { QuestionRenderer, type QuestionValue } from '@/components/QuestionRenderer'
import { Button } from '@/components/ui/button'
import { t as tr } from '@/lib/i18n'

function formatAnswer(question: PackQuestion, a: Answer | undefined): string {
  if (!a) return '—'
  if (a.skipped) return tr(getStoredLang(), 'skipped')
  if (question.type === 'number') {
    return a.answer_number == null ? '—' : String(a.answer_number)
  }
  if (question.type === 'checkbox_group') {
    if (!a.answer_choice) return '—'
    try {
      const parsed = JSON.parse(a.answer_choice)
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed.map(String).join(', ')
      }
    } catch {
      /* noop */
    }
    return '—'
  }
  if (question.type === 'radio') {
    return a.answer_choice || '—'
  }
  return a.answer_text || '—'
}

export function InviteReview() {
  const params = useParams() as { token: string }
  const token = params.token
  const [, setLocation] = useLocation()

  const [pack, setPack] = useState<Pack | null>(null)
  const [answers, setAnswers] = useState<Record<string, Answer>>({})
  const [editing, setEditing] = useState<string | null>(null)
  const [editValue, setEditValue] = useState<QuestionValue>({})
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    getInvitation(token, getStoredLang())
      .then((res) => {
        if (cancelled) return
        if (res.invitation.status === 'completed') {
          setLocation(`/i/${token}/done`)
          return
        }
        setPack(res.pack)
        const map: Record<string, Answer> = {}
        for (const a of res.answers) map[a.question_id] = a
        setAnswers(map)
      })
      .catch(() => {
        if (!cancelled) setError('load_failed')
      })
    return () => {
      cancelled = true
    }
  }, [token, setLocation])

  const totalAnswered = useMemo(
    () =>
      Object.values(answers).filter((a) => !a.skipped && hasContent(a)).length,
    [answers],
  )

  function hasContent(a: Answer): boolean {
    return Boolean(a.answer_text || a.answer_choice || a.answer_number != null)
  }

  function startEdit(q: PackQuestion) {
    const existing = answers[q.id]
    setEditing(q.id)
    setEditValue({
      answer_text: existing?.answer_text ?? null,
      answer_choice: existing?.answer_choice ?? null,
      answer_number: existing?.answer_number ?? null,
    })
  }

  async function saveEdit(q: PackQuestion) {
    setSaving(true)
    try {
      const res = await saveAnswer(token, {
        question_id: q.id,
        answer_text: editValue.answer_text ?? null,
        answer_choice: editValue.answer_choice ?? null,
        answer_number: editValue.answer_number ?? null,
        skipped: false,
      })
      setAnswers((prev) => ({ ...prev, [q.id]: res.answer }))
      setEditing(null)
    } catch {
      setError('save_failed')
    } finally {
      setSaving(false)
    }
  }

  const lang = getStoredLang()

  if (error === 'load_failed') {
    return (
      <main className="min-h-screen bg-cream flex items-center justify-center px-6 py-10">
        <p className="font-serif text-muted-foreground">{tr(lang, 'err_load')}</p>
      </main>
    )
  }

  if (!pack) {
    return (
      <main className="min-h-screen bg-cream flex items-center justify-center px-6 py-10">
        <p className="font-serif text-muted-foreground">{tr(lang, 'loading')}</p>
      </main>
    )
  }

  const totalQuestions = pack.sections.reduce(
    (n, s) => n + s.questions.length,
    0,
  )

  return (
    <main className="min-h-screen bg-cream px-4 sm:px-6 py-8 sm:py-12">
      <div className="mx-auto max-w-2xl space-y-8">
        <header className="space-y-2">
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
            {tr(lang, 'review_eyebrow')}
          </p>
          <h1 className="font-serif text-3xl sm:text-4xl">{pack.title}</h1>
          <p className="text-sm text-muted-foreground">
            {tr(lang, 'answered_count', { n: totalAnswered, m: totalQuestions })} · {tr(lang, 'tap_to_edit')}
          </p>
        </header>

        <div className="space-y-6">
          {pack.sections.map((section) => (
            <section key={section.key} className="space-y-3">
              <h2 className="font-serif text-xl text-foreground/80 border-b border-black/10 pb-1">
                {section.title}
              </h2>
              <div className="space-y-2">
                {section.questions.map((q) => {
                  const a = answers[q.id]
                  if (editing === q.id) {
                    return (
                      <div
                        key={q.id}
                        className="rounded-md border border-vermillion/40 bg-white p-4 space-y-3"
                      >
                        <QuestionRenderer
                          question={q}
                          value={editValue}
                          onChange={(next) =>
                            setEditValue((prev) => ({ ...prev, ...next }))
                          }
                        />
                        <div className="flex items-center justify-end gap-2 pt-2">
                          <Button
                            variant="secondary"
                            onClick={() => setEditing(null)}
                            disabled={saving}
                          >
                            {tr(lang, 'cancel')}
                          </Button>
                          <Button
                            onClick={() => saveEdit(q)}
                            disabled={saving}
                            className="bg-vermillion hover:bg-vermillion/90 text-white"
                          >
                            {saving ? tr(lang, 'saving') : tr(lang, 'save')}
                          </Button>
                        </div>
                      </div>
                    )
                  }
                  return (
                    <button
                      key={q.id}
                      type="button"
                      onClick={() => startEdit(q)}
                      className="w-full text-left rounded-md border border-transparent hover:border-black/10 hover:bg-white/60 p-3 transition-colors"
                    >
                      <p className="font-serif text-base leading-snug text-foreground">
                        {q.prompt}
                      </p>
                      <p
                        className={`text-sm mt-1 whitespace-pre-wrap ${
                          a && !a.skipped && hasContent(a)
                            ? 'text-foreground/80'
                            : 'text-muted-foreground italic'
                        }`}
                      >
                        {formatAnswer(q, a)}
                      </p>
                    </button>
                  )
                })}
              </div>
            </section>
          ))}
        </div>

        {error === 'save_failed' && (
          <p className="text-sm text-destructive">{tr(lang, 'err_save')}</p>
        )}

        <div className="flex items-center justify-between gap-3 pt-4 border-t border-black/5">
          <Button
            variant="secondary"
            onClick={() => setLocation(`/i/${token}`)}
          >
            {tr(lang, 'back_to_start')}
          </Button>
          <Button
            size="lg"
            onClick={() => setLocation(`/i/${token}/submit`)}
            className="bg-vermillion hover:bg-vermillion/90 text-white"
          >
            {tr(lang, 'submit')}
          </Button>
        </div>
      </div>
    </main>
  )
}

export default InviteReview
